import type { ConfigComponent } from "./types";
import type {
  ModelComponentWithDefaults,
  ComponentTypeMaterialWithName,
  AttributeDefWithAttribute,
} from "@/types/supabase-helpers";

interface BuildOptions {
  /** Asetetaan jokaiseen komponenttiin – näkyy UI-badgena */
  sourceModelName?: string;
  /** true = Math.max(1, min_quantity). Käytetään tuonnissa koska tuodut komponentit ovat tarkoituksella valittuja. */
  forceMinQuantity1?: boolean;
}

/**
 * Rakentaa ConfigComponent[]-taulukon model_components-datasta.
 * Käytetään sekä OrderConfiguratorTab:n initialisoinnissa että ModelImporter:n tuonnissa.
 */
export function buildConfigComponents(
  components: ModelComponentWithDefaults[],
  materials: ComponentTypeMaterialWithName[],
  attributeDefs: AttributeDefWithAttribute[],
  options?: BuildOptions
): ConfigComponent[] {
  return components.map((mc) => {
    const componentType = mc.component_types;

    // Get allowed materials for this component type
    const allowedMaterials = materials
      .filter((m) => m.component_type_id === mc.component_type_id)
      .map((m) => ({
        id: m.material_id,
        name: m.materials?.name || "Tuntematon",
        isDefault: m.is_default,
      }));

    // Get default material
    const defaultMaterial = allowedMaterials.find((m) => m.isDefault) || allowedMaterials[0];

    // Get attribute definitions for this component type
    const attrDefs = attributeDefs
      .filter((ad) => ad.component_type_id === mc.component_type_id)
      .map((ad) => {
        const attr = ad.attributes;
        return {
          id: crypto.randomUUID(),
          attributeId: ad.attribute_id,
          name: attr?.name || "",
          label: attr?.label || "",
          type: attr?.attribute_type || "text",
          isRequired: ad.is_required || attr?.is_required || false,
          options: attr?.options ? (attr.options as (string | { label: string; value: string; [key: string]: unknown })[]) : null,
          value: ad.default_value || null,
        };
      });

    const quantity = options?.forceMinQuantity1
      ? Math.max(1, mc.min_quantity)
      : mc.min_quantity;

    return {
      id: crypto.randomUUID(),
      modelComponentId: mc.id,
      componentTypeId: mc.component_type_id,
      componentTypeName: componentType?.name || "",
      componentTypeLabel: mc.label
        ? `${componentType?.label || ""} – ${mc.label}`
        : componentType?.label || "",
      dimensionMode: ((componentType?.dimension_mode || 'free') as 'free' | 'catalog' | 'none'),
      materialId: mc.default_material_id || defaultMaterial?.id || null,
      quantity,
      minQuantity: mc.min_quantity,
      maxQuantity: mc.max_quantity,
      width: mc.default_width != null ? mc.default_width : null,
      height: mc.default_height != null ? mc.default_height : null,
      thickness: mc.default_thickness != null ? mc.default_thickness : null,
      allowedMaterials,
      attributes: attrDefs,
      ...(options?.sourceModelName ? { sourceModelName: options.sourceModelName } : {}),
    };
  });
}
